import React, { useCallback, useState } from 'react';

import Text from '../../library/text/text';
import Tooltip from '../../library/tooltip/tooltip';
import Typography from '../../library/typography/typography';
import CalendarLinesRegularIcon from '../../shared/icons/calendar-lines-regular-icon';
import ClockRegularIcon from '../../shared/icons/clock-regular-icon';
import { ArrowsRepeatRegularIcon } from '../../shared/icons/icons';
import TireLifeCounter from '../tire-life-counter/tire-life-counter';

import { calculateLifeExpectancy } from '../../shared/utils/treadwell/treadwell';

import { ProductData } from '../../shared/types/graphql-types';

import './miles-driven-annually-info.scss';

const LIFE_OF_TIRE_TOOLTIP =
  'We calculate tire life estimates by dividing its median mileage by the number of miles you drive per year.';
const ANNUAL_OWNERSHIP_COST_TOOLTIP =
  'Calculated using the cost per mile of using this tire multiplied by your annual driving miles. A tire with a higher upfront cost may have a lower usage cost overtime by being more fuel efficient and lasting long, thus saving on fuel, replacement, and installation costs.';
const MAX_ANNUAL_MILES = 50000;
const MIN_ANNUAL_MILES = 0;

interface MilesDrivenAnnuallyInfoProps {
  costPerMile?: number;
  months?: number;
  product?: ProductData;
  showTooltip?: boolean;
  years?: number;
}

function MilesDrivenAnnuallyInfo(props: MilesDrivenAnnuallyInfoProps) {
  const { costPerMile, months, product, showTooltip = false, years } = props;
  const [annualMiles, setAnnualMiles] = useState(30000);

  const annualOwnershipCost =
    costPerMile && annualMiles ? costPerMile * annualMiles : 0;
  const { tireLifeMonths, tireLifeYears } = calculateLifeExpectancy(
    product,
    15 * 1000,
    Number(annualMiles || 0),
    months,
    years
  );

  const onChangeAnnualMiles = useCallback(e => {
    const value = parseInt(e.target.value);

    if (!isNaN(value)) {
      if (value < MIN_ANNUAL_MILES) {
        setAnnualMiles(MIN_ANNUAL_MILES);
      } else if (value > MAX_ANNUAL_MILES) {
        setAnnualMiles(MAX_ANNUAL_MILES);
      } else {
        setAnnualMiles(value);
      }
    } else {
      setAnnualMiles(MIN_ANNUAL_MILES);
    }
  }, []);

  return (
    <div data-testid="miles-driven-annually-info" styleName="container">
      <div styleName="title">
        <CalendarLinesRegularIcon styleName="icon" />
        <Typography weight="bold">Miles Driven Annually</Typography>
      </div>
      <Typography>
        Tell us how many miles you drive annually to see life of tire and annual
        tire life-cycle expense:
      </Typography>
      <Text
        inputMode="numeric"
        isRequired
        label="Estimated annual miles"
        name="annual-miles"
        onChange={onChangeAnnualMiles}
        styleName="annual-miles-input"
        type="number"
        value={annualMiles?.toString()}
      />
      <div styleName="tire-life-cost-container">
        <div styleName="tire-life-container">
          <div styleName="title-secondary">
            <ClockRegularIcon styleName="icon" />
            <Typography weight="bold">Life of Tire</Typography>
            {showTooltip && (
              <Tooltip content={LIFE_OF_TIRE_TOOLTIP} styleName="tooltip" />
            )}
          </div>
          <div styleName="year-month-container">
            {typeof tireLifeYears === 'number' && (
              <div>
                <TireLifeCounter countTo={tireLifeYears} isWholeNum start>
                  {({ value }) => (
                    <span
                      data-testid="miles-driven-annually-info__tire-life-years"
                      styleName="tire-life-value"
                    >
                      {value}
                    </span>
                  )}
                </TireLifeCounter>
                <span styleName="tire-life-label">years</span>
              </div>
            )}

            <div>
              {typeof tireLifeMonths === 'number' ? (
                <TireLifeCounter countTo={tireLifeMonths} isWholeNum start>
                  {({ value }) => (
                    <span
                      data-testid="miles-driven-annually-info__tire-life-months"
                      styleName="tire-life-value"
                    >
                      {value}
                    </span>
                  )}
                </TireLifeCounter>
              ) : (
                <span styleName="tire-life-value-na">N/A</span>
              )}
              <span styleName="tire-life-label">months</span>
            </div>
          </div>
        </div>
        {Boolean(annualOwnershipCost) && (
          <div styleName="cost-container">
            <div styleName="title-secondary">
              <ArrowsRepeatRegularIcon styleName="icon" />
              <Typography weight="bold">Annual Ownership Cost</Typography>
              {showTooltip && (
                <Tooltip
                  content={ANNUAL_OWNERSHIP_COST_TOOLTIP}
                  styleName="tooltip"
                />
              )}
            </div>
            <Typography styleName="annual-cost-value" weight="bold">
              ${annualOwnershipCost.toFixed(2)}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
}

export default MilesDrivenAnnuallyInfo;
